import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import usePremiumAccess from '../../hooks/usePremiumAccess';
import { AiStudyGenerationApi } from '../../services/ai/AiStudyGenerationApi';
import { AiAudioGenerationApi } from '../../services/ai/AiAudioGenerationApi';
import AudioUrlService from '../../services/AudioUrlService';
import { useToast } from '../../contexts/ToastContext';
import { useAudio } from '../../contexts/AudioContext';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import UpgradeModal from '../common/UpgradeModal';
import './StudyToolsTab.css';

/**
 * StudyToolsTab - Display and generate study tools for a specific document
 * Shows Quizzes, Flashcards, Podcasts, and Audiobooks filtered by document ID
 */
/**
 * BlockedPlaceholder – inline blocked-state matching the ScoutIQ tool pattern.
 * Shown in place of the generate button + content list when the user's tier is insufficient.
 */
function BlockedPlaceholder({ icon, title, description, requiredTier, onUnlock }) {
  const tierLabel = requiredTier === 'pro' ? 'Pro' : 'Basic';
  return (
    <div className="study-tool-blocked">
      <span className="study-tool-blocked__icon">{icon}</span>
      <h4 className="study-tool-blocked__title">{title}</h4>
      <p className="study-tool-blocked__description">{description}</p>
      <span className={`study-tool-blocked__tier-badge study-tool-blocked__tier-badge--${requiredTier}`}>
        Requires {tierLabel} plan
      </span>
      <button className="ui-btn ui-btn--primary ui-btn--sm study-tool-blocked__cta" onClick={onUnlock}>
        Unlock This Tool
      </button>
      <p className="study-tool-blocked__hint">Takes under 30 seconds</p>
    </div>
  );
}

/**
 * AudioBlockedPlaceholder – branded Scout + audio preview inline gate
 * for Podcasts & Audiobooks (Basic → Pro upgrade).
 */
function AudioBlockedPlaceholder({ documentName }) {
  const navigate = useNavigate();
  const displayName = documentName
    ? documentName.replace(/\.[^/.]+$/, '')
    : 'Chapter Summary';
  return (
    <div className="audio-gate">
      {/* Scout header */}
      <div className="audio-gate__scout-row">
        <img
          src="/scout_images/scout_thumbs_up.png"
          alt=""
          className="audio-gate__scout-img"
          draggable="false"
        />
        <div className="audio-gate__scout-bubble">
          I&rsquo;ll turn this into something you can listen to.
        </div>
      </div>

      <h3 className="audio-gate__title">Unlock audio learning</h3>
      <p className="audio-gate__subtitle">Turn your documents into podcasts and audiobooks.</p>

      {/* Audio preview card */}
      <div className="audio-gate__preview">
        <div className="audio-gate__play">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </div>
        <div className="audio-gate__info">
          <span className="audio-gate__audio-title">{displayName} &ndash; 6:42</span>
          <span className="audio-gate__audio-sub">Generated from your document</span>
        </div>
        <div className="audio-gate__wave" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
      </div>

      <ul className="audio-gate__features">
        <li>
          <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span>Turn your notes into audiobooks</span>
        </li>
        <li>
          <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span>Auto-generate study podcasts</span>
        </li>
        <li>
          <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span>Listen instead of rereading</span>
        </li>
        <li>
          <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span>Study while walking, driving, or lifting</span>
        </li>
      </ul>

      <p className="audio-gate__includes">Includes everything in Locked-In</p>

      <div className="audio-gate__cta-wrap">
        <button className="audio-gate__cta" onClick={() => navigate('/subscription')}>
          Go All-Gas for $11.99/month
        </button>
      </div>

      <p className="audio-gate__proof">Most students unlock this before their first exam.</p>
    </div>
  );
}

export default function StudyToolsTab({ documentId, documentName }) {
  const { user } = useAuth();
  const { listQuizSets, listFlashcardDecks, listAudioContent, deleteQuizSet, deleteFlashcardDeck, deleteAudioContent, getQuizSet, startQuizAttempt, submitQuizAnswers, getAttemptResults, clearCache } = useData();
  const { addToast } = useToast();
  const { playAudio: globalPlayAudio } = useAudio();
  const { subscription, initialized } = useSubscription();
  const { canCreate, tier: currentTier } = usePremiumAccess();

  // Subscription gating state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState({ requiredTier: 'basic', triggerSource: 'default' });

  // Compute creation-blocked status per tool type
  const isQuizzesBlocked = initialized && !canCreate('quizzes');
  const isFlashcardsBlocked = initialized && !canCreate('flashcards');
  const isPodcastsBlocked = initialized && !canCreate('podcasts');
  const isAudiobooksBlocked = initialized && !canCreate('audiobooks');

  const openUpgradeModal = (requiredTier, triggerSource) => {
    setUpgradeContext({ requiredTier, triggerSource });
    setShowUpgradeModal(true);
  };
  
  // Study content state
  const [quizzes, setQuizzes] = useState([]);
  const [flashcards, setFlashcards] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [audiobooks, setAudiobooks] = useState([]);
  
  // Loading states
  const [loadingQuizzes, setLoadingQuizzes] = useState(true);
  const [loadingFlashcards, setLoadingFlashcards] = useState(true);
  const [loadingPodcasts, setLoadingPodcasts] = useState(true);
  const [loadingAudiobooks, setLoadingAudiobooks] = useState(true);
  
  // Generation states
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [generatingFlashcards, setGeneratingFlashcards] = useState(false);
  const [generatingPodcast, setGeneratingPodcast] = useState(false);
  const [generatingAudiobook, setGeneratingAudiobook] = useState(false);
  
  // Expanded sections
  const [, setExpandedSections] = useState({
    quizzes: false,
    flashcards: false,
    podcasts: false,
    audiobooks: false,
  });

  // Active tool navigation (null = card grid, 'quizzes' | 'flashcards' | 'podcasts' | 'audiobooks')
  const [activeTool, setActiveTool] = useState(null);
  const [toolTransition, setToolTransition] = useState(''); // '' | 'entering' | 'entered'

  const openTool = (tool) => {
    setActiveTool(tool);
    setToolTransition('entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setToolTransition('entered'));
    });
  };

  const closeTool = () => {
    setToolTransition('');
    setTimeout(() => setActiveTool(null), 200);
  };

  // Selected items for viewing
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedFlashcardDeck, setSelectedFlashcardDeck] = useState(null);
  
  // Quiz taking state
  const [quizAttemptId, setQuizAttemptId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_quizMode, setQuizMode] = useState('taking'); // 'taking', 'summary', 'review'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [isGrading, setIsGrading] = useState(false);
  
  // Flashcard study state
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);
  const [flashcardMarked, setFlashcardMarked] = useState({});
  const [flashcardViewed, setFlashcardViewed] = useState({});

  // Load quizzes for this document
  const loadQuizzes = useCallback(async () => {
    if (!documentId) return;
    
    try {
      setLoadingQuizzes(true);
      console.log('[StudyToolsTab.loadQuizzes] Loading quizzes for documentId:', documentId);
      const data = await listQuizSets({ documentId, page: 0, size: 50 });
      console.log('[StudyToolsTab.loadQuizzes] Response:', data);
      console.log('[StudyToolsTab.loadQuizzes] Quiz sets count:', data.quizSets?.length || 0); 
      setQuizzes(data.quizSets || []);
    } catch (error) {
      console.error('[StudyToolsTab.loadQuizzes] Error loading quizzes:', error);
      setQuizzes([]);
    } finally {
      setLoadingQuizzes(false);
    }
  }, [documentId, listQuizSets]);

  // Load flashcards for this document
  const loadFlashcards = useCallback(async () => {
    if (!documentId) return;
    
    try {
      setLoadingFlashcards(true);
      const data = await listFlashcardDecks({ documentId, page: 0, size: 50 });
      setFlashcards(data.decks || []);
    } catch (error) {
      console.error('Error loading flashcards:', error);
      setFlashcards([]);
    } finally {
      setLoadingFlashcards(false);
    }
  }, [documentId, listFlashcardDecks]);

  // Load podcasts for this document
  const loadPodcasts = useCallback(async () => {
    if (!documentId) return;
    
    try {
      setLoadingPodcasts(true);
      const data = await listAudioContent({ format: 'podcast', page: 0, size: 100 });
      // Filter by sourceDocumentIds since audio can be generated from multiple docs
      const filtered = (data.audioContents || []).filter(audio => {
        const sourceIds = audio.sourceDocumentIds || audio.source_document_ids || [];
        return sourceIds.includes(documentId);
      });
      setPodcasts(filtered);
    } catch (error) {
      console.error('Error loading podcasts:', error);
      setPodcasts([]);
    } finally {
      setLoadingPodcasts(false);
    }
  }, [documentId, listAudioContent]);

  // Load audiobooks for this document
  const loadAudiobooks = useCallback(async () => {
    if (!documentId) return;
    
    try {
      setLoadingAudiobooks(true);
      const data = await listAudioContent({ format: 'audiobook', page: 0, size: 100 });
      // Filter by sourceDocumentIds on the frontend since backend filters by documentId not sourceDocumentIds
      const filtered = (data.audioContents || []).filter(audio => {
        const sourceIds = audio.sourceDocumentIds || audio.source_document_ids || [];
        return sourceIds.includes(documentId);
      });
      setAudiobooks(filtered);
    } catch (error) {
      console.error('Error loading audiobooks:', error);
      setAudiobooks([]);
    } finally {
      setLoadingAudiobooks(false);
    }
  }, [documentId, listAudioContent]);

  // Load all study tools on mount and when documentId changes
  useEffect(() => {
    if (documentId) {
      loadQuizzes();
      loadFlashcards();
      loadPodcasts();
      loadAudiobooks();
    }
  }, [documentId, loadQuizzes, loadFlashcards, loadPodcasts, loadAudiobooks]);

  // Toggle section expansion
  // eslint-disable-next-line no-unused-vars
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Generate quiz from this document
  const handleGenerateQuiz = async () => {
    if (isQuizzesBlocked) { openUpgradeModal('basic', 'quiz'); return; }
    console.log('[StudyToolsTab] Generate quiz clicked', { documentId, userId: user?.id, user });
    if (!documentId) {
      console.error('[StudyToolsTab] No documentId available');
      addToast({ intent: 'danger', message: 'No document ID available' });
      return;
    }
    
    try {
      setGeneratingQuiz(true);
      addToast({ intent: 'info', message: 'Generating quiz from this document...' });
      
      const corrId = UsageEventsTracker.startCorrelation('study_quiz');
      UsageEventsTracker.recordUserAction('QUIZ_GENERATION_STARTED', {
        action: 'generate',
        category: 'ai_tool',
        entityType: 'document',
        entityId: documentId,
        label: 'study_tab_quiz',
        metadata: { documentId, source: 'study_tools_tab' }
      }, { correlationId: corrId });
      
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[StudyToolsTab] Using userId:', userId);
      
      const payload = {
        document_ids: [Number(documentId)],
        num_questions: 10,
        difficulty_level: 'intermediate',
        user_id: userId,
        title: `${new Date().toLocaleDateString()}`,
        description: 'Generated from document'
      };
      
      console.log('[StudyToolsTab] Quiz generation payload:', payload);
      const response = await AiStudyGenerationApi.generateQuiz(payload);
      console.log('[StudyToolsTab] Quiz generation response:', response);
      
      // Optimistically add the new quiz to the list
      const newQuiz = {
        id: response.quiz_set_id,
        title: payload.title,
        description: payload.description,
        difficultyLevel: payload.difficulty_level,
        numQuestions: response.num_questions || response.questions?.length || 0,
        num_questions: response.num_questions || response.questions?.length || 0,
        createdAt: new Date().toISOString(),
        generationCostUsd: response.cost_usd
      };
      setQuizzes(prev => [newQuiz, ...prev]);
      
      UsageEventsTracker.recordUserAction('QUIZ_GENERATION_SUCCESS', {
        action: 'complete',
        category: 'ai_tool',
        entityType: 'quiz',
        entityId: response.quiz_set_id,
        label: 'study_tab_quiz',
        value: response.num_questions || response.questions?.length || 0,
        metadata: { documentId, quizId: response.quiz_set_id, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_quiz') });
      UsageEventsTracker.clearCorrelation('study_quiz');
      addToast({ intent: 'success', message: '📝 Quiz generated successfully!' });
      // Clear quiz cache to force fresh data fetch
      clearCache('quiz-sets');
      // Reload to get full data from backend
      await loadQuizzes();
    } catch (error) {
      console.error('Error generating quiz:', error);
      UsageEventsTracker.recordUserAction('QUIZ_GENERATION_FAILED', {
        action: 'error',
        category: 'ai_tool',
        label: 'study_tab_quiz',
        metadata: { documentId, errorMessage: error.message, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_quiz') });
      UsageEventsTracker.clearCorrelation('study_quiz');
      addToast({ intent: 'danger', message: error.message || 'Failed to generate quiz' });
    } finally {
      setGeneratingQuiz(false);
    }
  };

  // Generate flashcards from this document
  const handleGenerateFlashcards = async () => {
    if (isFlashcardsBlocked) { openUpgradeModal('basic', 'quiz'); return; }
    console.log('[StudyToolsTab] Generate flashcards clicked', { documentId, userId: user?.id, user });
    if (!documentId) {
      console.error('[StudyToolsTab] No documentId available');
      addToast({ intent: 'danger', message: 'No document ID available' });
      return;
    }
    
    try {
      setGeneratingFlashcards(true);
      addToast({ intent: 'info', message: 'Generating flashcards from this document...' });
      
      const corrId = UsageEventsTracker.startCorrelation('study_flashcard');
      UsageEventsTracker.recordUserAction('FLASHCARD_GENERATION_STARTED', {
        action: 'generate',
        category: 'ai_tool',
        entityType: 'document',
        entityId: documentId,
        label: 'study_tab_flashcards',
        metadata: { documentId, source: 'study_tools_tab' }
      }, { correlationId: corrId });
      
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[StudyToolsTab] Using userId:', userId);
      
      const payload = {
        document_ids: [Number(documentId)],
        num_cards: 20,
        user_id: userId,
        title: 'Flashcard Deck',
        description: 'Generated from document'
      };
      
      console.log('[StudyToolsTab] Flashcard generation payload:', payload);
      const response = await AiStudyGenerationApi.generateFlashcards(payload);
      console.log('[StudyToolsTab] Flashcard generation response:', response);
      
      // Optimistically add the new deck to the list
      const newDeck = {
        id: response.deck_id,
        title: payload.title,
        description: payload.description,
        numCards: response.num_cards || response.flashcards?.length || 0,
        createdAt: new Date().toISOString(),
        generationCostUsd: response.cost_usd
      };
      setFlashcards(prev => [newDeck, ...prev]);
      
      UsageEventsTracker.recordUserAction('FLASHCARD_GENERATION_SUCCESS', {
        action: 'complete',
        category: 'ai_tool',
        entityType: 'flashcard_deck',
        entityId: response.deck_id,
        label: 'study_tab_flashcards',
        value: response.num_cards || response.flashcards?.length || 0,
        metadata: { documentId, deckId: response.deck_id, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_flashcard') });
      UsageEventsTracker.clearCorrelation('study_flashcard');
      addToast({ intent: 'success', message: '🃏 Flashcards generated successfully!' });
      // Clear flashcard cache to force fresh data fetch
      clearCache('flashcard');
      await loadFlashcards(); // Reload flashcards list
    } catch (error) {
      console.error('Error generating flashcards:', error);
      UsageEventsTracker.recordUserAction('FLASHCARD_GENERATION_FAILED', {
        action: 'error',
        category: 'ai_tool',
        label: 'study_tab_flashcards',
        metadata: { documentId, errorMessage: error.message, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_flashcard') });
      UsageEventsTracker.clearCorrelation('study_flashcard');
      addToast({ intent: 'danger', message: error.message || 'Failed to generate flashcards' });
    } finally {
      setGeneratingFlashcards(false);
    }
  };

  // Generate podcast from this document
  const handleGeneratePodcast = async () => {
    if (isPodcastsBlocked) { openUpgradeModal('pro', 'audio'); return; }
    console.log('[StudyToolsTab] Generate podcast clicked', { documentId, userId: user?.id, user });
    if (!documentId) {
      console.error('[StudyToolsTab] No documentId available');
      addToast({ intent: 'danger', message: 'No document ID available' });
      return;
    }
    
    try {
      setGeneratingPodcast(true);
      addToast({ intent: 'info', message: 'Generating podcast from this document...' });
      
      const corrId = UsageEventsTracker.startCorrelation('study_podcast');
      UsageEventsTracker.recordUserAction('PODCAST_GENERATION_STARTED', {
        action: 'generate',
        category: 'ai_tool',
        entityType: 'document',
        entityId: documentId,
        label: 'study_tab_podcast',
        metadata: { documentId, source: 'study_tools_tab' }
      }, { correlationId: corrId });
      
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[StudyToolsTab] Using userId:', userId);
      
      const payload = {
        document_ids: [Number(documentId)],
        format: 'podcast',
        user_id: userId,
        title: 'Document Podcast',
        description: 'Generated from document',
        depth_level: 'standard' // Default depth level
      };
      
      console.log('[StudyToolsTab] Podcast generation payload:', payload);
      const response = await AiAudioGenerationApi.generateAudio(payload);
      console.log('[StudyToolsTab] Podcast generation response:', response);
      
      // Optimistically add the new podcast to the list
      const newPodcast = {
        id: response.audio_id || response.id,
        title: payload.title,
        description: payload.description,
        format: 'podcast',
        sourceDocumentIds: payload.document_ids,
        createdAt: new Date().toISOString(),
        generationCostUsd: response.cost_usd,
        status: response.status || 'completed'
      };
      setPodcasts(prev => [newPodcast, ...prev]);
      
      UsageEventsTracker.recordUserAction('PODCAST_GENERATION_SUCCESS', {
        action: 'complete',
        category: 'ai_tool',
        entityType: 'audio',
        entityId: response.audio_id || response.id,
        label: 'study_tab_podcast',
        metadata: { documentId, audioId: response.audio_id || response.id, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_podcast') });
      UsageEventsTracker.clearCorrelation('study_podcast');
      addToast({ intent: 'success', message: '🎙️ Podcast generated successfully!' });
      // Clear audio cache to force fresh data fetch
      clearCache('audio-content');
      await loadPodcasts(); // Reload podcasts list
    } catch (error) {
      console.error('Error generating podcast:', error);
      UsageEventsTracker.recordUserAction('PODCAST_GENERATION_FAILED', {
        action: 'error',
        category: 'ai_tool',
        label: 'study_tab_podcast',
        metadata: { documentId, errorMessage: error.message, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_podcast') });
      UsageEventsTracker.clearCorrelation('study_podcast');
      addToast({ intent: 'danger', message: error.message || 'Failed to generate podcast' });
    } finally {
      setGeneratingPodcast(false);
    }
  };

  // Generate audiobook from this document
  const handleGenerateAudiobook = async () => {
    if (isAudiobooksBlocked) { openUpgradeModal('pro', 'audio'); return; }
    console.log('[StudyToolsTab] Generate audiobook clicked', { documentId, userId: user?.id, user });
    if (!documentId) {
      console.error('[StudyToolsTab] No documentId available');
      addToast({ intent: 'danger', message: 'No document ID available' });
      return;
    }
    
    try {
      setGeneratingAudiobook(true);
      addToast({ intent: 'info', message: 'Generating audiobook from this document...' });
      
      const corrId = UsageEventsTracker.startCorrelation('study_audiobook');
      UsageEventsTracker.recordUserAction('AUDIOBOOK_GENERATION_STARTED', {
        action: 'generate',
        category: 'ai_tool',
        entityType: 'document',
        entityId: documentId,
        label: 'study_tab_audiobook',
        metadata: { documentId, source: 'study_tools_tab' }
      }, { correlationId: corrId });
      
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[StudyToolsTab] Using userId:', userId);
      
      const payload = {
        document_ids: [Number(documentId)],
        format: 'audiobook',
        user_id: userId,
        title: 'Document Audiobook',
        description: 'Generated from document',
        length_level: 'standard' // Default length level
      };
      
      console.log('[StudyToolsTab] Audiobook generation payload:', payload);
      const response = await AiAudioGenerationApi.generateAudio(payload);
      console.log('[StudyToolsTab] Audiobook generation response:', response);
      
      // Optimistically add the new audiobook to the list
      const newAudiobook = {
        id: response.audio_id || response.id,
        title: payload.title,
        description: payload.description,
        format: 'audiobook',
        sourceDocumentIds: payload.document_ids,
        createdAt: new Date().toISOString(),
        generationCostUsd: response.cost_usd,
        status: response.status || 'completed'
      };
      setAudiobooks(prev => [newAudiobook, ...prev]);
      
      UsageEventsTracker.recordUserAction('AUDIOBOOK_GENERATION_SUCCESS', {
        action: 'complete',
        category: 'ai_tool',
        entityType: 'audio',
        entityId: response.audio_id || response.id,
        label: 'study_tab_audiobook',
        metadata: { documentId, audioId: response.audio_id || response.id, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_audiobook') });
      UsageEventsTracker.clearCorrelation('study_audiobook');
      addToast({ intent: 'success', message: '📖 Audiobook generated successfully!' });
      // Clear audio cache to force fresh data fetch
      clearCache('audio-content');
      await loadAudiobooks(); // Reload audiobooks list
    } catch (error) {
      console.error('Error generating audiobook:', error);
      UsageEventsTracker.recordUserAction('AUDIOBOOK_GENERATION_FAILED', {
        action: 'error',
        category: 'ai_tool',
        label: 'study_tab_audiobook',
        metadata: { documentId, errorMessage: error.message, source: 'study_tools_tab' }
      }, { correlationId: UsageEventsTracker.getCorrelation('study_audiobook') });
      UsageEventsTracker.clearCorrelation('study_audiobook');
      addToast({ intent: 'danger', message: error.message || 'Failed to generate audiobook' });
    } finally {
      setGeneratingAudiobook(false);
    }
  };

  // Delete quiz
  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Delete this quiz? This action cannot be undone.')) return;
    
    try {
      await deleteQuizSet(quizId);
      addToast({ intent: 'success', message: 'Quiz deleted' });
      await loadQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      addToast({ intent: 'danger', message: 'Failed to delete quiz' });
    }
  };

  // Delete flashcard deck
  const handleDeleteFlashcards = async (deckId) => {
    if (!window.confirm('Delete this flashcard deck? This action cannot be undone.')) return;
    
    try {
      await deleteFlashcardDeck(deckId);
      addToast({ intent: 'success', message: 'Flashcard deck deleted' });
      await loadFlashcards();
    } catch (error) {
      console.error('Error deleting flashcard deck:', error);
      addToast({ intent: 'danger', message: 'Failed to delete flashcard deck' });
    }
  };

  // Delete audio content
  const handleDeleteAudio = async (audioId, type) => {
    if (!window.confirm(`Delete this ${type}? This action cannot be undone.`)) return;
    
    try {
      await deleteAudioContent(audioId);
      addToast({ intent: 'success', message: `${type.charAt(0).toUpperCase() + type.slice(1)} deleted` });
      if (type === 'podcast') {
        await loadPodcasts();
      } else {
        await loadAudiobooks();
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      addToast({ intent: 'danger', message: `Failed to delete ${type}` });
    }
  };

  // Open quiz in AI Tools page
  const handleOpenQuiz = async (quiz) => {
    try {
      addToast({ intent: 'info', message: 'Loading quiz...' });
      
      // Fetch full quiz with questions
      const quizSet = await getQuizSet(quiz.id);
      
      // Debug logging
      console.log('Quiz data loaded:', quizSet);
      if (quizSet.questions && quizSet.questions.length > 0) {
        console.log('First question:', quizSet.questions[0]);
        console.log('Question type:', quizSet.questions[0].question_type || quizSet.questions[0].questionType);
        console.log('Options:', quizSet.questions[0].options);
      }
      
      // Create new attempt
      const attemptResponse = await startQuizAttempt(quiz.id);
      console.log('[handleOpenQuiz] attemptResponse:', attemptResponse);
      
      // Parse response if needed
      const attemptData = attemptResponse.json ? await attemptResponse.json() : attemptResponse;
      console.log('[handleOpenQuiz] attemptData:', attemptData);
      
      // Set quiz for inline viewing
      setSelectedQuiz({ ...quizSet, quiz_set_id: quiz.id });
      setQuizAttemptId(attemptData.id);
      console.log('[handleOpenQuiz] Set quizAttemptId to:', attemptData.id);
      setQuizMode('taking');
      setCurrentQuestionIndex(0);
      setQuizAnswers({});
      setQuizSubmitted(false);
      setQuizResults(null);
      
      // Track quiz started for analytics pipelines
      UsageEventsTracker.recordUserAction('QUIZ_STARTED', {
        action: 'start',
        category: 'study',
        entityType: 'quiz',
        entityId: quiz.id,
        value: quizSet.questions?.length || 0,
        metadata: { documentId, questionCount: quizSet.questions?.length || 0, source: 'study_tools_tab' }
      });
      
      addToast({ intent: 'success', message: 'Quiz loaded!' });
    } catch (error) {
      console.error('Error opening quiz:', error);
      addToast({ intent: 'danger', message: 'Failed to open quiz' });
    }
  };

  // Open flashcard deck in AI Tools page
  const handleOpenFlashcards = async (deck) => {
    try {
      // If deck doesn't have cards loaded, we might need to fetch them
      // For now, assume the deck object has cards
      setSelectedFlashcardDeck(deck);
      setCurrentFlashcardIndex(0);
      setFlashcardFlipped(false);
      setFlashcardMarked({});
      setFlashcardViewed({});
      
      addToast({ intent: 'success', message: 'Flashcard deck loaded!' });
    } catch (error) {
      console.error('Error opening flashcards:', error);
      addToast({ intent: 'danger', message: 'Failed to open flashcards' });
    }
  };

  // Open podcast via global persistent player
  const handleOpenPodcast = async (podcast) => {
    const docId = podcast.documentId || podcast.document_id;
    if (!docId) { addToast({ intent: 'danger', message: 'Podcast audio not available' }); return; }
    try {
      const streamUrl = await AudioUrlService.getOrRefreshUrl(docId);
      globalPlayAudio({
        id: podcast.id,
        title: podcast.title || 'Podcast',
        audioUrl: streamUrl,
        format: 'podcast',
        durationSeconds: podcast.durationSeconds || podcast.duration_seconds || 0,
        documentId: docId
      });
      addToast({ intent: 'success', message: 'Playing podcast!' });
    } catch (err) {
      console.error('Failed to play podcast:', err);
      addToast({ intent: 'danger', message: 'Failed to load podcast audio' });
    }
  };

  // Open audiobook via global persistent player
  const handleOpenAudiobook = async (audiobook) => {
    const docId = audiobook.documentId || audiobook.document_id;
    if (!docId) { addToast({ intent: 'danger', message: 'Audiobook audio not available' }); return; }
    try {
      const streamUrl = await AudioUrlService.getOrRefreshUrl(docId);
      globalPlayAudio({
        id: audiobook.id,
        title: audiobook.title || 'Audiobook',
        audioUrl: streamUrl,
        format: 'audiobook',
        durationSeconds: audiobook.durationSeconds || audiobook.duration_seconds || 0,
        documentId: docId
      });
      addToast({ intent: 'success', message: 'Playing audiobook!' });
    } catch (err) {
      console.error('Failed to play audiobook:', err);
      addToast({ intent: 'danger', message: 'Failed to load audiobook audio' });
    }
  };

  // Submit quiz answers
  const handleSubmitQuiz = async () => {
    console.log('[StudyToolsTab] handleSubmitQuiz called');
    console.log('[StudyToolsTab] quizAttemptId:', quizAttemptId);
    console.log('[StudyToolsTab] selectedQuiz:', selectedQuiz);
    console.log('[StudyToolsTab] quizAnswers:', quizAnswers);
    
    if (!quizAttemptId || !selectedQuiz) {
      console.error('[StudyToolsTab] Missing quizAttemptId or selectedQuiz');
      return;
    }
    
    try {
      addToast({ intent: 'info', message: 'Submitting quiz...' });
      
      UsageEventsTracker.recordUserAction('QUIZ_SUBMIT', {
        action: 'submit',
        category: 'ai_tool',
        entityType: 'quiz_attempt',
        entityId: quizAttemptId,
        label: 'quiz_submit',
        value: selectedQuiz.questions?.length || 0,
        metadata: { quizId: selectedQuiz?.id, attemptId: quizAttemptId, source: 'study_tools_tab' }
      });
      
      // Build submissions array from answers state
      const submissions = selectedQuiz.questions.map((question, index) => ({
        questionId: question.id,
        userAnswer: quizAnswers[index] || ''
      }));
      
      console.log('[StudyToolsTab] Submissions:', submissions);
      
      // Submit to backend
      const response = await submitQuizAnswers(quizAttemptId, { answers: submissions });
      console.log('[StudyToolsTab] Submit response:', response);
      
      // Parse response if it's a Response object
      const data = response.json ? await response.json() : response;
      console.log('[StudyToolsTab] Parsed response:', data);
      console.log('[StudyToolsTab] answerSubmissions:', data.answerSubmissions);
      if (data.answerSubmissions && data.answerSubmissions.length > 0) {
        console.log('[StudyToolsTab] Sample submission:', data.answerSubmissions[0]);
        const gradedSubmissions = data.answerSubmissions.filter(s => s.gradingResult);
        console.log('[StudyToolsTab] Submissions with AI grading:', gradedSubmissions.length, gradedSubmissions);
      }
      
      // Set submitted state and show results
      setQuizSubmitted(true);
      setQuizResults(data);
      let finalResultData = data; // track final graded results for QUIZ_COMPLETED event
      
      // Check if there are short-answer questions that need AI grading
      const shortAnswerSubmissions = data.answerSubmissions?.filter(s => s.isCorrect === null) || [];
      console.log('[StudyToolsTab] Short answer questions needing grading:', shortAnswerSubmissions.length);
      
      if (shortAnswerSubmissions.length > 0) {
        // Call AI service to grade short-answer questions
        setIsGrading(true);
        try {
          addToast({ intent: 'info', message: 'Grading short-answer questions with AI...' });
          
          console.log('[StudyToolsTab] Calling AI service to grade attempt:', quizAttemptId);
          
          const gradingResult = await AiStudyGenerationApi.gradeQuizAttempt(quizAttemptId);
          console.log('[StudyToolsTab] AI grading result:', gradingResult);
          
          // Reload the quiz attempt to get updated grading results
          const updatedAttempt = await getAttemptResults(quizAttemptId);
          const updatedData = updatedAttempt.json ? await updatedAttempt.json() : updatedAttempt;
          console.log('[StudyToolsTab] Updated attempt after AI grading:', updatedData);
          
          setQuizResults(updatedData);
          finalResultData = updatedData;
          addToast({ intent: 'success', message: 'Quiz graded successfully!' });
        } catch (error) {
          console.error('[StudyToolsTab] AI grading failed:', error);
          addToast({ intent: 'warning', message: 'AI grading failed, showing results without short-answer scores' });
        } finally {
          setIsGrading(false);
        }
      } else {
        addToast({ intent: 'success', message: 'Quiz submitted successfully!' });
      }
      
      // Emit QUIZ_COMPLETED with score for analytics pipelines
      // (learning session aggregation, user_learning_profile view, study funnel)
      try {
        const finalAnswers = finalResultData?.answerSubmissions || [];
        const correctCount = finalAnswers.filter(s => s.isCorrect === true).length;
        const totalCount = selectedQuiz?.questions?.length || finalAnswers.length || 0;
        const scorePercent = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
        
        UsageEventsTracker.recordUserAction('QUIZ_COMPLETED', {
          action: 'complete',
          category: 'study',
          entityType: 'quiz',
          entityId: selectedQuiz?.id || selectedQuiz?.quiz_set_id,
          value: scorePercent,
          metadata: {
            documentId,
            attemptId: quizAttemptId,
            questionCount: totalCount,
            correctCount,
            source: 'study_tools_tab'
          }
        });
      } catch (trackErr) {
        console.warn('[StudyToolsTab] Failed to track QUIZ_COMPLETED', trackErr);
      }
      
      // Reload quizzes to update stats
      await loadQuizzes();
    } catch (error) {
      console.error('[StudyToolsTab] Error submitting quiz:', error);
      addToast({ intent: 'danger', message: 'Failed to submit quiz' });
    }
  };

  // Close viewers
  const handleCloseQuiz = () => {
    setSelectedQuiz(null);
    setQuizAttemptId(null);
    setQuizMode('taking');
    setCurrentQuestionIndex(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizResults(null);
  };

  const handleCloseFlashcards = () => {
    // Track flashcard session completion for analytics pipelines
    if (selectedFlashcardDeck) {
      try {
        const totalCards = selectedFlashcardDeck.cards?.length || 0;
        const viewedCount = Object.keys(flashcardViewed).length;
        const markedCount = Object.keys(flashcardMarked).length;
        
        UsageEventsTracker.recordUserAction('FLASHCARD_SESSION_COMPLETED', {
          action: 'complete',
          category: 'study',
          entityType: 'flashcard_deck',
          entityId: selectedFlashcardDeck.id,
          value: totalCards,
          metadata: {
            documentId,
            totalCards,
            viewedCount,
            markedCount,
            completionRate: totalCards > 0 ? Math.round((viewedCount / totalCards) * 100) : 0,
            source: 'study_tools_tab'
          }
        });
      } catch (trackErr) {
        console.warn('[StudyToolsTab] Failed to track FLASHCARD_SESSION_COMPLETED', trackErr);
      }
    }
    setSelectedFlashcardDeck(null);
    setCurrentFlashcardIndex(0);
    setFlashcardFlipped(false);
    setFlashcardMarked({});
    setFlashcardViewed({});
  };

  const handleClosePodcast = () => {}; // eslint-disable-line no-unused-vars

  const handleCloseAudiobook = () => {}; // eslint-disable-line no-unused-vars

  if (!documentId) {
    return (
      <div className="study-tools-tab">
        <div className="study-tools-empty">
          <p>No document selected</p>
        </div>
      </div>
    );
  }

  /* ── Tool config map ──────────────────────────────── */
  const toolConfig = {
    quizzes: {
      key: 'quizzes',
      label: 'Quizzes',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>),
      count: quizzes.length,
      loading: loadingQuizzes,
      generating: generatingQuiz,
      onGenerate: handleGenerateQuiz,
      generateLabel: 'Generate Quiz',
      blocked: isQuizzesBlocked,
      blockedTier: 'basic',
      blockedDesc: 'Generate practice quizzes from your documents. Prepare faster for exams.',
      items: quizzes,
      renderItem: (quiz) => (
        <div
          key={quiz.id}
          className="study-tool-card study-tool-card--clickable"
          onClick={() => handleOpenQuiz(quiz)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenQuiz(quiz); } }}
        >
          <div className="tool-card-header">
            <h5 className="tool-card-title">{quiz.title}</h5>
            <div className="tool-card-actions">
              <button className="tool-card-btn tool-card-btn--delete" onClick={(e) => { e.stopPropagation(); handleDeleteQuiz(quiz.id); }} aria-label="Delete quiz" title="Delete quiz">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
          <div className="tool-card-meta">
            <span className="tool-card-stat">{quiz.num_questions || quiz.numQuestions} questions</span>
            {(quiz.difficulty_level || quiz.difficultyLevel) && <span className="tool-card-badge">{quiz.difficulty_level || quiz.difficultyLevel}</span>}
          </div>
        </div>
      ),
      emptyMsg: 'No quizzes yet. Generate one to get started!',
    },
    flashcards: {
      key: 'flashcards',
      label: 'Flashcards',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>),
      count: flashcards.length,
      loading: loadingFlashcards,
      generating: generatingFlashcards,
      onGenerate: handleGenerateFlashcards,
      generateLabel: 'Generate Flashcards',
      blocked: isFlashcardsBlocked,
      blockedTier: 'basic',
      blockedDesc: 'Create flashcard decks to master key concepts. Quick, active recall study.',
      items: flashcards,
      renderItem: (deck) => (
        <div
          key={deck.id}
          className="study-tool-card study-tool-card--clickable"
          onClick={() => handleOpenFlashcards(deck)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenFlashcards(deck); } }}
        >
          <div className="tool-card-header">
            <h5 className="tool-card-title">{deck.title}</h5>
            <div className="tool-card-actions">
              <button className="tool-card-btn tool-card-btn--delete" onClick={(e) => { e.stopPropagation(); handleDeleteFlashcards(deck.id); }} aria-label="Delete deck" title="Delete deck">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
          <div className="tool-card-meta">
            <span className="tool-card-stat">{deck.cards?.length || deck.num_cards || 0} cards</span>
          </div>
        </div>
      ),
      emptyMsg: 'No flashcard decks yet. Generate one to get started!',
    },
    podcasts: {
      key: 'podcasts',
      label: 'Podcasts',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>),
      count: podcasts.length,
      loading: loadingPodcasts,
      generating: generatingPodcast,
      onGenerate: handleGeneratePodcast,
      generateLabel: 'Generate Podcast',
      blocked: isPodcastsBlocked,
      blockedTier: 'pro',
      blockedDesc: 'Turn your materials into AI study podcasts. Learn while you\'re busy.',
      items: podcasts,
      renderItem: (podcast) => (
        <div
          key={podcast.id}
          className="study-tool-card study-tool-card--clickable"
          onClick={() => handleOpenPodcast(podcast)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenPodcast(podcast); } }}
        >
          <div className="tool-card-header">
            <h5 className="tool-card-title">{podcast.title}</h5>
            <div className="tool-card-actions">
              <button className="tool-card-btn tool-card-btn--delete" onClick={(e) => { e.stopPropagation(); handleDeleteAudio(podcast.id, 'podcast'); }} aria-label="Delete podcast" title="Delete podcast">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
          <div className="tool-card-meta">
            {(podcast.durationSeconds || podcast.duration_seconds) && (
              <span className="tool-card-stat">{Math.floor((podcast.durationSeconds || podcast.duration_seconds) / 60)} min</span>
            )}
          </div>
        </div>
      ),
      emptyMsg: 'No podcasts yet. Generate one to get started!',
    },
    audiobooks: {
      key: 'audiobooks',
      label: 'Audiobooks',
      icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>),
      count: audiobooks.length,
      loading: loadingAudiobooks,
      generating: generatingAudiobook,
      onGenerate: handleGenerateAudiobook,
      generateLabel: 'Generate Audiobook',
      blocked: isAudiobooksBlocked,
      blockedTier: 'pro',
      blockedDesc: 'Convert your documents into audiobooks. Listen and learn on the go.',
      items: audiobooks,
      renderItem: (audiobook) => (
        <div
          key={audiobook.id}
          className="study-tool-card study-tool-card--clickable"
          onClick={() => handleOpenAudiobook(audiobook)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenAudiobook(audiobook); } }}
        >
          <div className="tool-card-header">
            <h5 className="tool-card-title">{audiobook.title}</h5>
            <div className="tool-card-actions">
              <button className="tool-card-btn tool-card-btn--delete" onClick={(e) => { e.stopPropagation(); handleDeleteAudio(audiobook.id, 'audiobook'); }} aria-label="Delete audiobook" title="Delete audiobook">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
          <div className="tool-card-meta">
            {(audiobook.durationSeconds || audiobook.duration_seconds) && (
              <span className="tool-card-stat">{Math.floor((audiobook.durationSeconds || audiobook.duration_seconds) / 60)} min</span>
            )}
          </div>
        </div>
      ),
      emptyMsg: 'No audiobooks yet. Generate one to get started!',
    },
  };

  const activeConfig = activeTool ? toolConfig[activeTool] : null;

  return (
    <div className="study-tools-tab">
      {/* ── Card Grid (default) ────────────────────────── */}
      {!activeTool && (
        <div className="st-cards">
          <div className="st-cards__grid">
            {Object.values(toolConfig).map(tool => (
              <button
                key={tool.key}
                className="st-tool-card"
                onClick={() => openTool(tool.key)}
              >
                <span className="st-tool-card__icon">{tool.icon}</span>
                <span className="st-tool-card__name">{tool.label}</span>
                <span className="st-tool-card__count">
                  {tool.loading ? '...' : tool.count}
                </span>
                {tool.blocked && (
                  <span className={`st-tool-card__badge st-tool-card__badge--${tool.blockedTier}`}>
                    {tool.blockedTier === 'pro' ? 'PRO' : 'BASIC'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Tool Workspace ─────────────────────────────── */}
      {activeTool && activeConfig && (
        <div className={`st-workspace ${toolTransition === 'entered' ? 'st-workspace--visible' : ''}`}>
          {/* Header */}
          <div className="st-workspace__header">
            <button className="st-workspace__back" onClick={closeTool} title="Back to Study Tools">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              <span>Study Tools</span>
            </button>
            <h3 className="st-workspace__title">{activeConfig.label}</h3>
          </div>

          {/* Content */}
          <div className="st-workspace__body">
            <>
              {/* Generate button – show paywall modal when creation is blocked */}
              <div className="st-workspace__actions">
                <button
                  className="ui-btn ui-btn--primary ui-btn--sm"
                  onClick={() => {
                    if (activeConfig.blocked) {
                      openUpgradeModal(activeConfig.blockedTier, activeConfig.blockedTier === 'pro' ? 'audio' : 'quiz');
                    } else {
                      activeConfig.onGenerate();
                    }
                  }}
                  disabled={activeConfig.generating}
                >
                  {activeConfig.generating
                    ? 'Generating...'
                    : activeConfig.blocked
                    ? `🔒 ${activeConfig.generateLabel}`
                    : `+ ${activeConfig.generateLabel}`}
                </button>
              </div>

              {/* Items list – always shown so users can view history */}
              {activeConfig.loading ? (
                <div className="section-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading {activeConfig.label.toLowerCase()}...</p>
                </div>
              ) : activeConfig.items.length === 0 ? (
                <div className="section-empty">
                  <p>{activeConfig.emptyMsg}</p>
                </div>
              ) : (
                <div className="study-tool-list">
                  {activeConfig.items.map(activeConfig.renderItem)}
                </div>
              )}
            </>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier}
        requiredTier={upgradeContext.requiredTier}
        featureName={upgradeContext.triggerSource === 'audio' ? 'Audio Generation' : 'Study Tools'}
        triggerSource={upgradeContext.triggerSource}
      />

      {/* Inline Quiz Viewer */}
      {selectedQuiz && (
        <div className="study-tools-viewer-overlay">
          <div className="study-tools-viewer-modal">
            <div className="study-tools-viewer-header">
              <h3>{selectedQuiz.title || 'Quiz'}</h3>
              <button 
                className="study-tools-viewer-close"
                onClick={handleCloseQuiz}
                aria-label="Close quiz"
              >
                ✕
              </button>
            </div>
            <div className="study-tools-viewer-content">
              {!quizSubmitted ? (
                // Quiz Taking View
                <div className="quiz-taking-view">
                  <div className="quiz-progress">
                    Question {currentQuestionIndex + 1} of {selectedQuiz.questions?.length || 0}
                  </div>
                  {selectedQuiz.questions && selectedQuiz.questions[currentQuestionIndex] && (
                    <div className="quiz-question">
                      <p className="quiz-question-text">
                        {selectedQuiz.questions[currentQuestionIndex].question_text || 
                         selectedQuiz.questions[currentQuestionIndex].questionText}
                      </p>
                      
                      {/* Multiple choice / True False */}
                      {(selectedQuiz.questions[currentQuestionIndex].question_type === 'multiple_choice' || 
                        selectedQuiz.questions[currentQuestionIndex].question_type === 'true_false' ||
                        selectedQuiz.questions[currentQuestionIndex].questionType === 'multiple_choice' || 
                        selectedQuiz.questions[currentQuestionIndex].questionType === 'true_false') && (
                        <div className="quiz-choices">
                          {(() => {
                            const question = selectedQuiz.questions[currentQuestionIndex];
                            const questionType = question.question_type || question.questionType;
                            
                            // For true/false questions, generate True/False choices
                            let choices;
                            if (questionType === 'true_false') {
                              choices = ['True', 'False'];
                            } else {
                              choices = question.options?.choices || question.choices || [];
                            }
                            
                            if (choices.length > 0) {
                              return choices.map((choice, idx) => {
                                const letter = String.fromCharCode(65 + idx);
                                const isSelected = quizAnswers[currentQuestionIndex] === choice;
                                return (
                                  <button
                                    key={idx}
                                    className={`quiz-choice ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setQuizAnswers(prev => ({ ...prev, [currentQuestionIndex]: choice }))}
                                  >
                                    <span className="quiz-choice-letter">{letter}</span>
                                    <span className="quiz-choice-text">{choice}</span>
                                  </button>
                                );
                              });
                            } else {
                              return (
                                <div className="section-empty">
                                  <p>⚠️ No answer choices available for this question.</p>
                                  <p style={{ fontSize: '0.875rem', color: 'var(--semantic-text-muted)' }}>
                                    This quiz may have been generated incorrectly. Please try regenerating it or contact support.
                                  </p>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      )}
                      
                      {/* Short answer */}
                      {(selectedQuiz.questions[currentQuestionIndex].question_type === 'short_answer' ||
                        selectedQuiz.questions[currentQuestionIndex].questionType === 'short_answer') && (
                        <textarea
                          className="quiz-short-answer"
                          value={quizAnswers[currentQuestionIndex] || ''}
                          onChange={(e) => setQuizAnswers(prev => ({ ...prev, [currentQuestionIndex]: e.target.value }))}
                          placeholder="Type your answer..."
                          rows={4}
                        />
                      )}
                    </div>
                  )}
                  
                  <div className="quiz-navigation">
                    <button
                      className="ui-btn ui-btn--neutral"
                      onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                      disabled={currentQuestionIndex === 0}
                    >
                      Previous
                    </button>
                    {currentQuestionIndex < (selectedQuiz.questions?.length || 0) - 1 ? (
                      <button
                        className="ui-btn ui-btn--primary"
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        className="ui-btn ui-btn--primary"
                        onClick={handleSubmitQuiz}
                        disabled={Object.keys(quizAnswers).length < (selectedQuiz.questions?.length || 0)}
                      >
                        Submit Quiz
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                // Quiz Results View
                <div className="quiz-results-view">
                  <h4>Quiz Completed!</h4>
                  
                  {quizResults && (
                    <>
                      <div className="quiz-results-summary">
                        {isGrading ? (
                          <div className="quiz-grading-spinner">
                            <div className="loading-spinner"></div>
                            <p>Grading short-answer questions...</p>
                          </div>
                        ) : (
                          <>
                            <div className="quiz-score">
                              <h2>{quizResults.scorePercent}%</h2>
                              <p>Your Score</p>
                            </div>
                            {quizResults.answerSubmissions && (
                              <div className="quiz-results-breakdown">
                                <p>
                                  <strong>Correct:</strong> {quizResults.answerSubmissions.filter(s => s.isCorrect).length} / {quizResults.answerSubmissions.length}
                                </p>
                                {quizResults.gradingCostUsd > 0 && (
                                  <p className="quiz-grading-cost">
                                    AI Grading Cost: ${quizResults.gradingCostUsd.toFixed(4)}
                                  </p>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Detailed question review */}
                      {quizResults.answerSubmissions && (
                        <div className="quiz-results-details">
                          <h5>Question Review</h5>
                          {selectedQuiz.questions.map((question, index) => {
                            const submission = quizResults.answerSubmissions.find(s => s.questionId === question.id);
                            if (!submission) return null;
                            
                            const questionText = question.questionText || question.question_text;
                            const correctAnswer = question.correctAnswer || question.correct_answer;
                            const isCorrect = submission.isCorrect;
                            const isShortAnswer = question.questionType === 'SHORT_ANSWER' || question.question_type === 'SHORT_ANSWER';
                            const needsGrading = isGrading && isCorrect === null && isShortAnswer;
                            
                            return (
                              <div key={question.id} className={`quiz-result-item ${isCorrect ? 'correct' : isCorrect === false ? 'incorrect' : 'pending'}`}>
                                <div className="result-header">
                                  <h6>Question {index + 1}</h6>
                                  <span className={`result-badge ${isCorrect ? 'correct' : isCorrect === false ? 'incorrect' : 'graded'}`}>
                                    {isCorrect ? '✓ Correct' : isCorrect === false ? '✗ Incorrect' : 'Graded'}
                                  </span>
                                </div>
                                <p className="result-question">{questionText}</p>
                                {needsGrading ? (
                                  <div className="result-item-loading">
                                    <div className="loading-spinner"></div>
                                    <span>Grading this answer...</span>
                                  </div>
                                ) : (
                                  <>
                                    <div className="result-answers">
                                      <p className="result-your-answer">
                                        <strong>Your answer:</strong> {submission.userAnswer}
                                      </p>
                                      <p className="result-correct-answer">
                                        <strong>Correct answer:</strong> {correctAnswer}
                                      </p>
                                    </div>
                                    {submission.gradingResult && (
                                      <div className="result-grading">
                                        {submission.gradingResult.score !== undefined && (
                                          <p className="grading-score">
                                            <strong>Score:</strong> {submission.gradingResult.score}/100
                                          </p>
                                        )}
                                        {submission.gradingResult.reasoning && (
                                          <p className="grading-reasoning">
                                            <strong>AI Feedback:</strong> {submission.gradingResult.reasoning}
                                          </p>
                                        )}
                                        {submission.gradingResult.key_points_covered && submission.gradingResult.key_points_covered.length > 0 && (
                                          <div className="grading-points-covered">
                                            <strong>Key Points Covered:</strong>
                                            <ul>
                                              {submission.gradingResult.key_points_covered.map((point, i) => (
                                                <li key={i}>{point}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {submission.gradingResult.key_points_missed && submission.gradingResult.key_points_missed.length > 0 && (
                                      <div className="grading-points-missed">
                                        <strong>Key Points Missed:</strong>
                                        <ul>
                                          {submission.gradingResult.key_points_missed.map((point, i) => (
                                            <li key={i}>{point}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {question.explanation && (
                                  <div className="result-explanation">
                                    <strong>Explanation:</strong> {question.explanation}
                                  </div>
                                )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                  
                  <button
                    className="ui-btn ui-btn--primary"
                    onClick={handleCloseQuiz}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inline Flashcard Viewer */}
      {selectedFlashcardDeck && selectedFlashcardDeck.cards && selectedFlashcardDeck.cards.length > 0 && (
        <div className="study-tools-viewer-overlay">
          <div className="study-tools-viewer-modal">
            <div className="study-tools-viewer-header">
              <h3>{selectedFlashcardDeck.title || 'Flashcards'}</h3>
              <button 
                className="study-tools-viewer-close"
                onClick={handleCloseFlashcards}
                aria-label="Close flashcards"
              >
                ✕
              </button>
            </div>
            <div className="study-tools-viewer-content">
              <div className="flashcard-viewer-simple">
                <div className="flashcard-progress">
                  Card {currentFlashcardIndex + 1} of {selectedFlashcardDeck.cards.length}
                </div>
                
                <div 
                  className={`flashcard-card ${flashcardFlipped ? 'flipped' : ''}`}
                  onClick={() => setFlashcardFlipped(!flashcardFlipped)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      setFlashcardFlipped(!flashcardFlipped);
                    }
                  }}
                >
                  <div className="flashcard-side-label">
                    {flashcardFlipped ? 'BACK' : 'FRONT'}
                  </div>
                  <div className="flashcard-text">
                    {flashcardFlipped 
                      ? (selectedFlashcardDeck.cards[currentFlashcardIndex].back_text || 
                         selectedFlashcardDeck.cards[currentFlashcardIndex].backText)
                      : (selectedFlashcardDeck.cards[currentFlashcardIndex].front_text || 
                         selectedFlashcardDeck.cards[currentFlashcardIndex].frontText)}
                  </div>
                  <div className="flashcard-flip-hint">
                    Click to flip
                  </div>
                </div>
                
                <div className="flashcard-navigation">
                  <button
                    className="ui-btn ui-btn--neutral"
                    onClick={() => {
                      setCurrentFlashcardIndex(Math.max(0, currentFlashcardIndex - 1));
                      setFlashcardFlipped(false);
                    }}
                    disabled={currentFlashcardIndex === 0}
                  >
                    ← Previous
                  </button>
                  <button
                    className="ui-btn ui-btn--neutral"
                    onClick={() => {
                      setCurrentFlashcardIndex(Math.min(selectedFlashcardDeck.cards.length - 1, currentFlashcardIndex + 1));
                      setFlashcardFlipped(false);
                    }}
                    disabled={currentFlashcardIndex === selectedFlashcardDeck.cards.length - 1}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
